import MaterialTable, {Column} from 'material-table';
import React, {FunctionComponent, useEffect, useState} from 'react';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {getGlobal, useGlobal} from 'reactn';
import {useWindowHeight} from '@react-hook/window-size/throttled'
import {ActionWithAuthorization} from '../../auth/KeycloakState';
import {RetailerOptConfigRich} from '../../components/ConfigurationForm/ConfigurationFormSchema';
import useHelpers from '../../helpers/datetime';
import useFeatures from '../../helpers/features';
import ApiService from '../../services/ApiService';
import {CAN_CREATE, CAN_EDIT, CAN_VIEW} from '../../shared/constants/AuthConstants';
import {STATUS_LABEL, TYPE_LABEL} from '../../shared/constants/GeneralDataFormConstants';
import {format} from 'date-fns';
import {FormControl, Icon, Select} from '@material-ui/core';
import {ConversionType} from '../../gen/api';
import {appStyle} from '../../AppStyle';

interface ConfigurationsTableState {
    columns: Array<Column<RetailerOptConfigRich>>;
    data: RetailerOptConfigRich[];
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formControl: {
            margin: theme.spacing(1),
            minWidth: 120,
        },
        selectEmpty: {
            marginTop: theme.spacing(2),
        },
    }),
);

const { featureEnabledGrocerPointsConversion } = useFeatures();

const ConfigurationsList: FunctionComponent<RouteComponentProps> = ({ history }) => {
    const [globalState, setGlobalState] = useGlobal();
    const classes = useStyles();
    const [typeFilterState, setTypeFilterState] = React.useState<{ type: ConversionType; name: string }>({
        type: ConversionType.CURRENCYCONVERSION,
        name: TYPE_LABEL.get(ConversionType.CURRENCYCONVERSION) || '',
    });

    const [state, setState] = useState<ConfigurationsTableState>({
        columns: [
            {
                title: 'ID',
                field: 'id',
                customSort: (a, b) =>
                    Number(a.id ? a.id.replace('ROC', '') : '0') - Number(b.id ? b.id.replace('ROC', '') : '0'),
            },
            {
                title: 'Name',
                field: 'name',
                customSort: (a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1),
                cellStyle: appStyle.table.cell.nameStyle,
            },
            {
                title: 'Description',
                field: 'description',
                sorting: false,
                cellStyle: appStyle.table.cell.descriptionStyle,
            },
            {
                title: 'Participant/Sponsor',
                field: 'retailer.udk',
                customSort: (a, b) => (a.retailer!.udk.toLowerCase() < b.retailer!.udk.toLowerCase() ? -1 : 1),
            },
            {
                title: 'Start',
                field: 'startDateTime',
                type: 'datetime',
                render: (rowData) => format(new Date(rowData.startDateTime), 'MM/dd/yyyy hh:mm\xa0a'),
            },
            {
                title: 'End',
                field: 'endDateTime',
                type: 'datetime',
                render: (rowData) => format(new Date(rowData.endDateTime), 'MM/dd/yyyy hh:mm\xa0a'),
            },
            {
                title: 'Program',
                field: 'program.udk',
                customSort: (a, b) => (a.program!.udk.toLowerCase() < b.program!.udk.toLowerCase() ? -1 : 1),
            },
            { title: 'Status', field: 'status', render: (rowData) => STATUS_LABEL.get(rowData.status) },
            {
                title: 'Created',
                field: 'createdAt',
                type: 'datetime',
                render: (rowData) =>
                    rowData.createdAt ? format(new Date(rowData.createdAt), 'MM/dd/yyyy hh:mm\xa0a') : '',
            },
        ],
        data: [],
    });

    const [dataByType, setDataByType] = useState(
        new Map<ConversionType, RetailerOptConfigRich[]>([
            [ConversionType.CURRENCYCONVERSION, []],
            [ConversionType.GROCERPOINTSCONVERSION, []],
            [ConversionType.FISCONVERSION, []]
        ]),
    );

    const handleTypeFilterChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const name = event.target.name as keyof typeof typeFilterState;
        setTypeFilterState({
            ...typeFilterState,
            [name]: event.target.value,
        });

        setState({
            columns: state.columns,
            data: dataByType.get(event.target.value as ConversionType) || [],
        });
    };

    const [retailersLoaded, setRetailersLoaded] = useState(
        new Map<ConversionType, boolean>([
            [ConversionType.CURRENCYCONVERSION, false],
            [ConversionType.GROCERPOINTSCONVERSION, false],
            [ConversionType.FISCONVERSION, false]
        ]),
    );

    const [programsLoaded, setProgramsLoaded] = useState(
        new Map<ConversionType, boolean>([
            [ConversionType.CURRENCYCONVERSION, false],
            [ConversionType.GROCERPOINTSCONVERSION, false],
            [ConversionType.FISCONVERSION, false]
        ]),
    );

    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [dataLoadingError, setDataLoadingError] = useState<string | null>(null);

    const { getTime } = useHelpers();

    useEffect(() => {
        const fetchData = async () => {
            const configurations = await ApiService().fetchConfigurations();

            const configurationsRich: RetailerOptConfigRich[] = configurations.data.map((a) => {
                return {
                    ...a,
                    program: { id: a.programId, udk: a.programId.toString(), description: '' },
                    retailer: { id: a.retailerId, udk: a.retailerId.toString(), name: '' },
                    ruleInputs: {},
                };
            });

            const data = configurationsRich.sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt));

            setDataByType(
                dataByType.set(
                    ConversionType.CURRENCYCONVERSION,
                    data.filter((value) => value.type == ConversionType.CURRENCYCONVERSION),
                ),
            );
            setDataByType(
                dataByType.set(
                    ConversionType.GROCERPOINTSCONVERSION,
                    data.filter((value) => value.type == ConversionType.GROCERPOINTSCONVERSION),
                ),
            );

            setDataByType(
                dataByType.set(
                    ConversionType.FISCONVERSION,
                    data.filter((value) => value.type == ConversionType.FISCONVERSION),
                ),
            );

            setState({
                columns: state.columns,
                data: dataByType.get(typeFilterState.type) || [],
            });

            setDataLoaded(true);
        };
        fetchData()
            .catch(error => setDataLoadingError(`${error}`));
    }, []);

    async function loadPrograms() {
        const data = dataByType.get(typeFilterState.type);
        if (data) {
            await Promise.all(
                data.map(async (configuration: RetailerOptConfigRich) => {
                    try {
                        configuration.program = (await ApiService().fetchProgramById(configuration.programId)).data;
                    } catch (e) {
                        console.error(e);
                    }
                }),
            );

            setDataByType(dataByType.set(typeFilterState.type, data));
            setProgramsLoaded(programsLoaded.set(typeFilterState.type, true));

            setState({
                columns: state.columns,
                data: data,
            });
        }
    }

    async function loadRetailers() {
        const data = dataByType.get(typeFilterState.type);
        if (data) {
            await Promise.all(
                data.map(async (configuration: RetailerOptConfigRich) => {
                    try {
                        configuration.retailer = (await ApiService().fetchRetailerById(configuration.retailerId)).data;
                    } catch (e) {
                        console.error(e);
                    }
                }),
            );

            setDataByType(dataByType.set(typeFilterState.type, data));
            setRetailersLoaded(retailersLoaded.set(typeFilterState.type, true));

            setState({
                columns: state.columns,
                data: data,
            });
        }
    }

    useEffect(() => {
        const fetchPrograms = async () => {
            loadPrograms();
        };

        if (dataLoaded && !programsLoaded.get(typeFilterState.type)) {
            fetchPrograms();
        }
    }, [dataLoaded, typeFilterState.type]);

    useEffect(() => {
        const fetchRetailers = async () => {
            loadRetailers();
        };

        if (dataLoaded && !retailersLoaded.get(typeFilterState.type)) {
            fetchRetailers();
        }
    }, [dataLoaded, typeFilterState.type]);

    function getActionsAllowed(): any[] {
        let actionsAllowed = getGlobal().keycloakState.getActionsAllowed([
            {
                name: 'view',
                icon: 'visibility',
                title: 'View Item',
                url: '/edit-configuration',
                authorizationNeeded: CAN_VIEW,
            },
            {
                name: 'edit',
                icon: 'edit',
                title: 'Edit Item',
                url: '/edit-configuration',
                authorizationNeeded: CAN_EDIT,
            },
            {
                name: 'history',
                icon: 'history',
                title: 'Item Audit',
                url: '/audit-trails',
                authorizationNeeded: CAN_CREATE,
            },
            {
                name: 'add',
                icon: 'add',
                title: 'Add Item',
                isFreeAction: true,
                url: '/add-configuration',
                authorizationNeeded: CAN_CREATE,
            },
            {
                name: 'history-free',
                icon: 'history',
                title: 'Audit Log',
                isFreeAction: true,
                url: '/audit-trails',
                authorizationNeeded: CAN_CREATE,
            },
        ]);

        if (getGlobal().keycloakState.isAuthorized(CAN_EDIT)) {
            actionsAllowed = actionsAllowed.filter((element: ActionWithAuthorization) => element.name != 'view');
        }

        return actionsAllowed.map((element: ActionWithAuthorization) => {
            return {
                icon: element.icon,
                tooltip: element.title,
                isFreeAction: element.isFreeAction,
                onClick: element.isFreeAction
                    ? () => history.push(element.url)
                    : (_event: any, rowData: any) => history.push(`${element.url}/${rowData.id}`),
            };
        });
    }

    const conversionTypeDropdown = (<>{featureEnabledGrocerPointsConversion() && <>
        <FormControl className={classes.formControl}>
            <Select
                native
                value={typeFilterState.type}
                onChange={handleTypeFilterChange}
                inputProps={{
                    name: 'type',
                    id: 'type',
                }}
            >
                <option value={ConversionType.CURRENCYCONVERSION}>
                    {TYPE_LABEL.get(ConversionType.CURRENCYCONVERSION)}
                </option>
                <option value={ConversionType.GROCERPOINTSCONVERSION}>
                    {TYPE_LABEL.get(ConversionType.GROCERPOINTSCONVERSION)}
                </option>
                <option value={ConversionType.FISCONVERSION}>
                    {TYPE_LABEL.get(ConversionType.FISCONVERSION)}
                </option>
            </Select>
        </FormControl>
        <hr />
    </>}</>);

    const windowHeight = useWindowHeight();
    const canPinHeader = windowHeight >= appStyle.window.minHeightToUseStickyHeaderAndFooter;
    const pinHeader = globalState.pinHeader && canPinHeader;

    return (
        <div>
            <MaterialTable
                options={{
                    actionsColumnIndex: 9,
                    search: false,
                    paging: false,
                    draggable: true,
                    filtering: true,
                    // Sticky header isn't supported yet, so we use maxBodyHeight and position: 'sticky'
                    headerStyle: { position: 'sticky', ...appStyle.table.headerStyle },
                    maxBodyHeight: pinHeader ? 'calc(100vh - 170px)' : undefined,
                    exportButton: false,
                }}
                onRowClick={
                    // onRowClick is required to enable style on mouse hover
                    () => { }
                }
                title="Configurations List"
                columns={state.columns}
                data={state.data}
                actions={[
                    {
                        icon: () => conversionTypeDropdown,
                        tooltip: "Select Conversion Type",
                        isFreeAction: true,
                    },
                    ...getActionsAllowed(),
                    // Only display the button if the window height is large enough, and rotate icon while disabled
                    ...(canPinHeader ? [{
                        icon: pinHeader
                            ? "push_pin"
                            : () => <Icon style={{ transform: "rotate(45deg)" }}>push_pin</Icon>,
                        tooltip: 'Pin Header to Stay on Top',
                        isFreeAction: true,
                        onClick: () => { setGlobalState({ pinHeader: !pinHeader }) },
                    }] : []),
                ]}
                // Display the default message of "No records to display" only if the data was loaded successfully
                localization={dataLoaded ? undefined : {
                    body: {
                        emptyDataSourceMessage: dataLoadingError
                            ? `Loading the content failed - ${dataLoadingError}`
                            : "Loading the content...",
                    },
                }}
            />
        </div>
    );
};

export default withRouter(ConfigurationsList);
